/**
 * Results Display Component
 * Shows classification results with criteria, categories, confidence, and price range
 * 
 * Requirement: 15.2
 */

import { useState, useEffect } from 'react';
import { ClassificationResult, Subscription } from '../types';
import './ResultsDisplay.css';

interface ResultsDisplayProps {
  result: ClassificationResult | null;
  variant?: 'default' | 'modal';
  disableTooltips?: boolean;
  onSaveFeedback?: (frameId: string, feedback: string) => Promise<void>;
  subscription?: Subscription | null;
}

function ResultsDisplay({ result, variant = 'default', disableTooltips = false, onSaveFeedback, subscription }: ResultsDisplayProps) {
  const [expandedReasoning, setExpandedReasoning] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{top: number, left: number} | null>(null);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackDraft, setFeedbackDraft] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackSaved, setFeedbackSaved] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  useEffect(() => {
    setFeedbackOpen(false);
    setFeedbackDraft('');
    setFeedbackLoading(false);
    setFeedbackSaved(false);
    setFeedbackError(null);
  }, [result?.frame_id]);

  const handleFeedbackSubmit = async () => {
    if (!result || !onSaveFeedback || !feedbackDraft.trim()) return;
    setFeedbackLoading(true);
    setFeedbackError(null);
    try {
      await onSaveFeedback(result.frame_id, feedbackDraft.trim());
      setFeedbackSaved(true);
      setFeedbackOpen(false);
    } catch (err) {
      setFeedbackError(err instanceof Error ? err.message : 'Failed to save feedback');
    } finally {
      setFeedbackLoading(false);
    }
  };

  const getConfidenceClass = (confidence: number | null): string => {
    if (confidence == null) return '';
    const percentage = confidence * 100;
    if (percentage <= 50) return 'confidence-low';
    if (percentage < 80) return 'confidence-medium';
    return 'confidence-high';
  };

  const toggleReasoning = (index: number, event: React.MouseEvent) => {
    // If tooltips are disabled, do nothing
    if (disableTooltips) {
      return;
    }

    if (expandedReasoning === index) {
      setExpandedReasoning(null);
      setTooltipPosition(null);
    } else {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      
      // For Data Dashboard and other contexts, use scroll-aware positioning
      const spacing = 16;
      const top = rect.bottom + window.scrollY + spacing;
      const left = rect.left + window.scrollX + rect.width / 2;
      setTooltipPosition({ top, left });
      
      setExpandedReasoning(index);
    }
  };

  // Close tooltip when clicking outside - only for Data Dashboard click behavior
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (expandedReasoning !== null && !(event.target as Element).closest('.result-card')) {
        setExpandedReasoning(null);
        setTooltipPosition(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [expandedReasoning]);

  if (!result) {
    return (
      <div className="results-display results-empty" role="status" aria-label="No classification results">
        <div className="results-placeholder">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p>No classification yet</p>
          <span>Enable classification to see results</span>
        </div>
      </div>
    );
  }

  return (
    <article className={`results-display ${variant === 'modal' ? 'results-display-modal' : ''}`} role="region" aria-label="Classification results">
      {/* Main title with description and price - hide in modal variant since it's shown in header */}
      {variant === 'default' && (result.description || (subscription?.show_price && result.estimated_price != null)) && (
        <div className="results-header">
          <h3 className="results-title">
            {result.description || 'Classification Result'}
            {subscription?.show_price && result.estimated_price != null && (
              <span className="results-price"> (€{result.estimated_price.toFixed(2)})</span>
            )}
          </h3>
        </div>
      )}

      <div className="results-grid">
        {result.criteria_results.map((criterionResult, index) => {
          return (
            <div 
              key={index} 
              className="result-card" 
              onClick={!disableTooltips ? (e) => criterionResult.reasoning && toggleReasoning(index, e) : undefined}
            >
              <div className="result-card-main">
                <div className="result-label">
                  {criterionResult.criterion_name}
                </div>
                <div className="result-value-with-confidence">
                  <span className="result-value">
                    {criterionResult.status === 'classified' 
                      ? criterionResult.selected_category 
                      : '—'}
                  </span>
                  <span className={`result-confidence ${getConfidenceClass(criterionResult.confidence)}`}>
                    {criterionResult.confidence != null 
                      ? `${(criterionResult.confidence * 100).toFixed(0)}%`
                      : '—'}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* User feedback section */}
      {onSaveFeedback && (
        <div className="result-feedback-section">
          {feedbackOpen ? (
            <div className="result-feedback-form">
              <textarea
                className="result-feedback-textarea"
                rows={3}
                maxLength={1000}
                value={feedbackDraft}
                onChange={(e) => setFeedbackDraft(e.target.value)}
                placeholder="Describe what was wrong with this classification..."
              />
              <div className="result-feedback-char-count">{feedbackDraft.length}/1000</div>
              {feedbackError && <div className="result-feedback-error">{feedbackError}</div>}
              <div className="result-feedback-actions">
                <button
                  className="result-feedback-cancel"
                  onClick={() => { setFeedbackOpen(false); setFeedbackError(null); }}
                  disabled={feedbackLoading}
                >
                  Cancel
                </button>
                <button
                  className="result-feedback-submit"
                  onClick={handleFeedbackSubmit}
                  disabled={feedbackLoading || !feedbackDraft.trim()}
                >
                  {feedbackLoading ? 'Saving...' : 'Submit'}
                </button>
              </div>
            </div>
          ) : result.user_feedback ? (
            <div className="result-feedback-saved">
              <span className="result-feedback-saved-label">Your feedback:</span>
              <span className="result-feedback-saved-text">{result.user_feedback}</span>
              <button
                className="result-feedback-edit-btn"
                onClick={() => { setFeedbackDraft(result.user_feedback!); setFeedbackOpen(true); }}
              >
                Edit
              </button>
            </div>
          ) : (
            <button
              className="result-feedback-toggle"
              onClick={() => { setFeedbackDraft(''); setFeedbackOpen(true); }}
            >
              Leave feedback
            </button>
          )}
          {feedbackSaved && !feedbackOpen && !result.user_feedback && (
            <div className="result-feedback-success">Feedback saved!</div>
          )}
        </div>
      )}

      {/* Fixed position tooltip for Data Dashboard click behavior */}
      {!disableTooltips && expandedReasoning !== null && tooltipPosition && result.criteria_results[expandedReasoning]?.reasoning && (
        <div 
          className="result-reasoning-tooltip" 
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            transform: 'translateX(-50%)'
          }}
        >
          <div className="reasoning-content">
            {result.criteria_results[expandedReasoning].reasoning}
          </div>
          <div className="reasoning-arrow"></div>
        </div>
      )}
    </article>
  );
}

export default ResultsDisplay;
